import {
  CheckCircle,
  Palette,
  Microscope,
  ExternalLink,
  BookOpen,
} from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function Approach() {
  return (
    <div className="min-h-screen bg-gradient-to-b bg-primary to-green-400/10">
      <Helmet>
        <title>
          My Nutritional Therapy Approach | Evidence-Informed personalised
          Nutrition
        </title>
        <meta
          name="description"
          content="Discover how Aleks's evidence-informed nutritional therapy approach works: comprehensive assessment, personalised recommendations, and functional testing to achieve your health goals."
        />
        <meta
          name="keywords"
          content="nutritional therapy approach, personalised nutrition, assessment, functional testing, behavioral strategies, holistic nutrition"
        />
        <meta
          property="og:title"
          content="My Evidence-Informed Nutritional Therapy Approach"
        />
        <meta
          property="og:description"
          content="Learn about the three-step nutritional therapy process: Assessment, Personalisation, and Testing & Support to create your bespoke health programme."
        />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 md:pt-24 md:pb-16">
        {/* Page Header */}
        <div className="text-center mb-12 md:mb-16">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
            My Approach
          </h1>
          <div className="w-24 h-1 bg-gray-300 mx-auto rounded-full"></div>
        </div>

        {/* Introduction Card */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-10 lg:p-12">
            <p className="text-lg sm:text-xl text-gray-900 leading-relaxed font-semibold mb-4">
              What is Nutritional Therapy?
            </p>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              Nutritional therapy is a person centered interventions that
              applies nutrition and lifestyle approaches to benefit your health
              outcome.
            </p>
          </div>
        </div>

        {/* Approach Steps - 3 Column Layout */}
        <div className="max-w-6xl mx-auto mb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4">
            {/* Step 1 */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 sm:p-8 hover:shadow-xl transition-all duration-300 hover:scale-105">
              <div className="flex justify-center mb-4">
                <CheckCircle className="w-12 h-12 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Assessment
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                In collaboration with you, I will create a bespoke holistic
                evidence-informed programme that is specific to your goals,
                needs, and circumstances. We will explore your current symptoms
                and concerns, but also your medical and family history, current
                diet and lifestyle, environment in order to establish potential
                nutritional deficiencies or macronutrient status.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 sm:p-8 hover:shadow-xl transition-all duration-300 hover:scale-105">
              <div className="flex justify-center mb-4">
                <Palette className="w-12 h-12 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Personalisation
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Nutritional therapy implements behavioral strategies to help you
                make sustainable diet and lifestyle changes by identifying
                possible barriers and difficulties that might prevent your
                progress.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 sm:p-8 hover:shadow-xl transition-all duration-300 hover:scale-105">
              <div className="flex justify-center mb-4">
                <Microscope className="w-12 h-12 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                Testing & Support
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                In order to make the most informed decision on the optimal diet
                and lifestyle recommendations, I might suggest the use of more
                in-depth testing. These special lab tests are used as a tool to
                gather deeper insight into your nutritional status, and
                lifestyle factors rather than a substitute for conventional
                medical diagnoses.
              </p>
            </div>
          </div>
        </div>
        {/* image */}
        <div className="mb-12 md:mb-16">
          <div className="relative max-w-2xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-green-400/50 rounded-3xl transform -rotate-3"></div>
            <img
              src="/sashka2.jpg"
              alt="Aleksandra - Nutritional Therapist"
              className="relative rounded-3xl shadow-2xl w-full h-auto object-cover"
            />
          </div>
        </div>
        {/* Process Flow Section */}
        {/* <div className="max-w-4xl mx-auto mb-12">
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-10 lg:p-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 text-center pb-6 border-b-2 border-primary/20">
              Your Programme Includes:
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary text-white font-bold text-sm">
                    ✓
                  </div>
                </div>
                <p className="text-base sm:text-lg text-gray-700">
                  <span className="font-semibold text-gray-900">
                    Comprehensive health assessment
                  </span>{" "}
                  of your symptoms, medical history, and lifestyle factors
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary text-white font-bold text-sm">
                    ✓
                  </div>
                </div>
                <p className="text-base sm:text-lg text-gray-700">
                  <span className="font-semibold text-gray-900">
                    personalised nutritional and lifestyle recommendations
                  </span>{" "}
                  that fit seamlessly into your daily life
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary text-white font-bold text-sm">
                    ✓
                  </div>
                </div>
                <p className="text-base sm:text-lg text-gray-700">
                  <span className="font-semibold text-gray-900">
                    Behavioral strategies
                  </span>{" "}
                  to support sustainable changes and overcome barriers
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary text-white font-bold text-sm">
                    ✓
                  </div>
                </div>
                <p className="text-base sm:text-lg text-gray-700">
                  <span className="font-semibold text-gray-900">
                    Optional functional testing
                  </span>{" "}
                  for deeper insights and personalisation
                </p>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary text-white font-bold text-sm">
                    ✓
                  </div>
                </div>
                <p className="text-base sm:text-lg text-gray-700">
                  <span className="font-semibold text-gray-900">
                    Ongoing support and guidance
                  </span>{" "}
                  to help you stay on track and achieve your goals
                </p>
              </div>
            </div>
          </div>
        </div> */}
        <div className="bg-gradient-to-r from-pink-50/60 to-green-50/60 py-16 md:py-20">
          <div className="container max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
                Free Menopause Breakfast Guide
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                A practical PDF booklet with breakfast ideas to support
                metabolic changes during menopause.
              </p>
            </div>

            <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-10 lg:p-12">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <div className="inline-flex items-center gap-2 bg-pink-50 text-pink-700 px-4 py-2 rounded-full mb-4 text-sm font-semibold">
                    <BookOpen className="w-4 h-4" />
                    Free PDF Booklet
                  </div>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
                    Breakfast ideas to support metabolic changes during
                    menopause
                  </h3>
                  <p className="text-gray-600 leading-relaxed max-w-2xl">
                    These breakfasts are designed to support your blood glucose
                    levels, improve satiety and reduce cravings, address
                    hormonal health, and manage brain fog.
                  </p>
                </div>

                <a
                  href={
                    "/booklets/Breakfast ideas for metabolic health in menopause.pdf"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-primary to-green-600 hover:from-green-700 hover:to-green-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap"
                  aria-label="Open menopause breakfast booklet PDF"
                >
                  View Booklet
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
        {/* Call to Action */}
        <div className="mt-12 md:mt-16 text-center">
          <div className="bg-white/35 rounded-2xl p-8 sm:p-12 max-w-2xl mx-auto">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Ready to get started?
            </h3>
            <p className="text-base sm:text-lg text-gray-100 mb-6">
              Let's create a personalised nutrition plan tailored to your unique
              needs.
            </p>
            <button
              onClick={() =>
                window.open(
                  "https://www.bookable.page/aleksandra",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              className="bg-white text-primary hover:bg-gray-100 font-semibold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-base sm:text-lg"
            >
              Book Your Free Discovery Call
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
